import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import MainApi from "../../../../../../services/mainApi.service";
import { memo, useCallback, useMemo, useState } from "react";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { AppointmentArrival } from "./AppointmentArrivalForm/AppointmentArrival";
import { ITicketAutRecRuleArrivalRules } from "../../../../../../models/ticket-aut-rec-rule-arrival-rule";
import { AppointmentArrivalPageProps } from "./AppointmentArrivalForm/models";

const TICKET_AUT_REC_RULE_ARRIVAL_RULE_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutRecRuleArrivalRule/";
const ticketAutRecRuleArrivalRuleService =
  new MainApi<ITicketAutRecRuleArrivalRules>(
    TICKET_AUT_REC_RULE_ARRIVAL_RULE_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.appointmentArrival.";

const AppointmentArrivalPage = ({ type }: AppointmentArrivalPageProps) => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [classificationForm, setClassificationForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns = useMemo(() => {
    const baseColumns: GridColDef<ITicketAutRecRuleArrivalRules>[] = [
      {
        field: "appointmentArrivalRule",
        headerName:
          t(translationPath + "appointment_arrival_rule") ||
          "Appointment Arrival Rule",
        renderCell: (params) => {
          if (!params.value) return "-";
          return (
            <OverflowTooltip
              text={params.value.name}
              tooltip={params.value.name}
              textTransform="none"
              variant="body2"
            />
          );
        },
      },
      {
        field: "ticketAutRecRuleArrivalRuleEstablishments",
        headerName: t(translationPath + "establishments") || "Establishments",
        renderCell: ({
          row: { ticketAutRecRuleArrivalRuleEstablishments },
        }) => {
          const establishments = ticketAutRecRuleArrivalRuleEstablishments
            ?.map((p) => p.externalName)
            .join("; ");
          if (!establishments) return "-";
          return (
            <OverflowTooltip
              text={establishments}
              tooltip={establishments}
              textTransform="none"
              variant="body2"
            />
          );
        },
      },
      {
        field: "ticketAutRecRuleArrivalRuleInsurances",
        headerName: t(translationPath + "insurances") || "Insurances",
        renderCell: ({ row: { ticketAutRecRuleArrivalRuleInsurances } }) => {
          const insurances = ticketAutRecRuleArrivalRuleInsurances
            ?.map((p) => p.externalName)
            .join("; ");
          if (!insurances) return "-";
          return (
            <OverflowTooltip
              text={insurances}
              tooltip={insurances}
              textTransform="none"
              variant="body2"
            />
          );
        },
      },
    ];

    if (type === "consultation") {
      baseColumns.push(
        {
          field: "ticketAutRecRuleArrivalRuleCalendars",
          headerName: t(translationPath + "calendars") || "Appointments",
          renderCell: ({ row: { ticketAutRecRuleArrivalRuleCalendars } }) => {
            const calendars = ticketAutRecRuleArrivalRuleCalendars
              ?.map((p) => p.externalName)
              .join("; ");
            if (!calendars) return "-";
            return (
              <OverflowTooltip
                text={calendars}
                tooltip={calendars}
                textTransform="none"
                variant="body2"
              />
            );
          },
        },
        {
          field: "ticketAutRecRuleArrivalRuleClassifications",
          headerName:
            t(translationPath + "classifications") || "Classifications",
          renderCell: ({
            row: { ticketAutRecRuleArrivalRuleClassifications },
          }) => {
            const classifications = ticketAutRecRuleArrivalRuleClassifications
              ?.map((p) => p.externalName)
              .join("; ");
            if (!classifications) return "-";
            return (
              <OverflowTooltip
                text={classifications}
                tooltip={classifications}
                textTransform="none"
                variant="body2"
              />
            );
          },
        },
      );
    } else {
      baseColumns.push({
        field: "ticketAutRecRuleArrivalRuleProcedures",
        headerName:
          t(translationPath + "internal_procedures") || "Internal Procedures",
        renderCell: ({ row: { ticketAutRecRuleArrivalRuleProcedures } }) => {
          const procedures = ticketAutRecRuleArrivalRuleProcedures
            ?.map((p) => p.externalName)
            .join("; ");
          if (!procedures) return "-";
          return (
            <OverflowTooltip
              text={procedures}
              tooltip={procedures}
              textTransform="none"
              variant="body2"
            />
          );
        },
      });
    }

    return baseColumns;
  }, [t, type]);

  const handleAdd = () => {
    setClassificationForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutRecRuleArrivalRules>,
  ) => {
    if (!params.row) return;
    setClassificationForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setClassificationForm(false);
  }, []);

  const props = useMemo(() => {
    return {
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
      tasyScheduleTypeCode: type === "consultation" ? "3" : "2",
    };
  }, [ticketAutomationReceivedRuleId, type]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {classificationForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <AppointmentArrival
            id={selectedId}
            type={type}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!classificationForm && (
        <Box height={600}>
          <Table<ITicketAutRecRuleArrivalRules>
            showDefaultMacroFilters={false}
            service={ticketAutRecRuleArrivalRuleService}
            columns={columns}
            title={""}
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(
                translationPath +
                  (type === "consultation"
                    ? "add_appointment_arrival_consultation"
                    : "add_appointment_arrival_exam"),
              ) || "Add Appointment Arrival"
            }
            confirmOptions={{
              title: t(
                translationPath +
                  (type === "consultation"
                    ? "confirm.title_consultation"
                    : "confirm.title_exam"),
              ),
              description: t(
                translationPath +
                  (type === "consultation"
                    ? "confirm.description_consultation"
                    : "confirm.description_exam"),
              ),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(AppointmentArrivalPage);
